import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AlertCircle, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatCount } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import type * as API from "@shared-core/data/api-types";
import { IssueStatusBadge, IssueTypeBadge } from "../issues/issueBadges";
import { currentTime } from "@shared-core/domain/current-time";

const PREVIEW_COUNT = 5;
const PREVIEW_WINDOW_DAYS = 60;

/**
 * Customer-360's "Improvement logs" section — the customer-facing name for
 * this app's unified Issues surface (quality issues, complaints, delivery/
 * production problems, improvement suggestions; platform-native + legacy
 * CRM). Scoped to the last {@link PREVIEW_WINDOW_DAYS} days so an
 * account with a long history doesn't crowd out what's actually current;
 * "View all" links to the full, unscoped, filterable Issues list for this
 * customer.
 */
export function CustomerImprovementLogs({
  customerId,
}: {
  customerId: string;
}) {
  const dataAdapter = useDataAdapter();
  const [issues, setIssues] = useState<API.IssueViewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const raisedAfter = new Date(
      currentTime().getTime() - PREVIEW_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
    dataAdapter
      .listIssues({
        customerId,
        pageSize: PREVIEW_COUNT,
        sort: "-raised_at",
        raisedAfter,
      })
      .then((result) => {
        if (cancelled) return;
        setIssues(result.items);
        setTotal(result.total);
      })
      .catch(() => {
        if (cancelled) return;
        setIssues([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, customerId]);

  if (loading) {
    return (
      <section
        aria-label="Improvement logs"
        className="rounded-lg border border-border bg-muted/30 p-4"
      >
        <div className="text-sm text-muted-foreground">
          Loading improvement logs…
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Improvement logs"
      className="rounded-lg border border-border bg-muted/30 p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <AlertCircle className="size-4 text-muted-foreground" aria-hidden />
          <span className="font-medium">Improvement logs</span>
          {total > 0 && (
            <span className="text-muted-foreground">
              ({formatCount(total)})
            </span>
          )}
        </div>
        <Link
          to={`${paths.issues()}?customer=${customerId}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          View all
          <ChevronRight className="size-3.5" />
        </Link>
      </div>

      {issues.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No improvement logs for this customer in the last{" "}
          {PREVIEW_WINDOW_DAYS} days.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-background">
          {issues.map((issue) => (
            <li key={issue.id}>
              <Link
                to={paths.issueDetail({ id: issue.id })}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-muted/50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <IssueTypeBadge type={issue.type_id} />
                  <span className="truncate">{issue.title}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(issue.raised_at), {
                      addSuffix: true,
                    })}
                  </span>
                  <IssueStatusBadge status={issue.status} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
